import { useEffect, useMemo, useState } from 'react';
import Input from '../../../../shared/components/Input/Input';
import Dialog from '../../../../shared/components/Dialog/Dialog';
import { formatAffairAmount } from '../affairFormatters';
import { getAffairStage } from '../affairModel';
import { hasValidationErrors, validateStageTransition, validateWonAffair } from '../affairValidation';

export const AFFAIR_LOSS_REASONS = [
  'Prix', 'Délais', 'Charge indisponible', 'Compétences', 'Concurrent', 'Projet abandonné',
  'Réponse non conforme', 'Aucune réponse client', 'Autre',
];

const today = () => new Date().toISOString().slice(0, 10);
const quoteAmount = (quote) => quote.totalClientAmount ?? quote.total_client_amount ?? quote.totalAmount ?? quote.total_amount ?? '';
const quoteLabel = (quote) => `${quote.quoteNumber || quote.quote_number || `Devis ${quote.id}`} — ${quote.title || 'Devis'} (${formatAffairAmount(quoteAmount(quote))})`;

const emptyForm = (toStage) => ({
  toStage,
  reason: '',
  competitor: '',
  comment: '',
  acceptedQuoteId: '',
  acceptedAmount: '',
  acceptedAt: today(),
  orderReference: '',
  desiredStartDate: '',
});

export default function AffairTransitionDialog({ affair, loading = false, onClose, onSubmit, open, quotes = [], toStage }) {
  const [form, setForm] = useState(() => emptyForm(toStage));
  const [errors, setErrors] = useState({});
  const isWon = toStage === 'won';
  const needsReason = ['lost', 'abandoned'].includes(toStage);
  const title = useMemo(() => toStage ? `${getAffairStage(affair?.stage).label} → ${getAffairStage(toStage).label}` : 'Changer d’étape', [affair?.stage, toStage]);

  useEffect(() => {
    if (open) {
      setForm(emptyForm(toStage));
      setErrors({});
    }
  }, [open, toStage]);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined, reason: name === 'reason' ? undefined : current.reason }));
  };

  const selectQuote = (event) => {
    const selected = quotes.find((quote) => String(quote.id) === event.target.value);
    setForm((current) => ({ ...current, acceptedQuoteId: event.target.value, acceptedAmount: selected ? quoteAmount(selected) : '' }));
    setErrors((current) => ({ ...current, acceptedQuoteId: undefined, acceptedAmount: undefined }));
  };

  const submit = () => {
    const validationErrors = isWon ? validateWonAffair(affair, form) : validateStageTransition(affair, form);
    setErrors(validationErrors);
    if (!hasValidationErrors(validationErrors)) onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} onSubmit={submit} title={title} submitLabel="Confirmer le changement" loading={loading} size="medium">
      <div className="affair-transition-form">
        {needsReason && (
          <div className={`custom-input custom-input--full ${errors.reason ? 'custom-input--error' : ''}`}>
            <label className="custom-input__label" htmlFor="affair-transition-reason">Motif *</label>
            <select id="affair-transition-reason" name="reason" className="custom-input__field" value={form.reason} onChange={update}>
              <option value="">Sélectionner</option>
              {AFFAIR_LOSS_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </select>
            {errors.reason && <span className="custom-input__help">{errors.reason}</span>}
          </div>
        )}
        {toStage === 'lost' && <Input fullWidth label="Concurrent retenu" name="competitor" value={form.competitor} onChange={update} />}
        {needsReason && <Input fullWidth multiline rows={3} label="Commentaire" name="comment" value={form.comment} onChange={update} />}
        {isWon && (
          <>
            <div className={`custom-input custom-input--full ${errors.acceptedQuoteId ? 'custom-input--error' : ''}`}>
              <label className="custom-input__label" htmlFor="affair-accepted-quote">Devis retenu *</label>
              <select id="affair-accepted-quote" className="custom-input__field" value={form.acceptedQuoteId} onChange={selectQuote}>
                <option value="">Sélectionner</option>
                {quotes.map((quote) => <option key={quote.id} value={quote.id}>{quoteLabel(quote)}</option>)}
              </select>
              {errors.acceptedQuoteId && <span className="custom-input__help">{errors.acceptedQuoteId}</span>}
            </div>
            <Input fullWidth required type="number" min="0" label="Montant retenu" name="acceptedAmount" value={form.acceptedAmount} onChange={update} error={errors.acceptedAmount} />
            <Input fullWidth required type="date" label="Date d’acceptation" name="acceptedAt" value={form.acceptedAt} onChange={update} error={errors.acceptedAt} />
            <Input fullWidth label="Référence de commande" name="orderReference" value={form.orderReference} onChange={update} />
            <Input fullWidth type="date" label="Date de démarrage envisagée" name="desiredStartDate" value={form.desiredStartDate} onChange={update} />
          </>
        )}
      </div>
    </Dialog>
  );
}
